import React, { useEffect, useState } from "react";
import AssetLibrary from "./asset-library/AssetLibrary";
import { createPortal } from "react-dom";

import { ActionsMediaAssetsLab } from "../../../../../Actions/ActionsMediaAssetsLab/ActionsMediaAssetsLab";
import { MediaAssetsLabStore, withRefluxStore } from "../../../../../Stores";

import { Collapse, Group } from "../../../../../Components/Layout";
import { CollapseHeader } from "../CollapseHeader";
import AssetPreview from "./asset-preview/AssetPreview";
import { getImageDimensions, isVideo } from "../../utils";
import { getVideoDuration } from "../../TemplatePost/TemplatePost__Preview/utils";
import { SelectInput } from "../../../../../Components/Form";
import { LOGO_POSITION_OPTIONS, LOGO_SIZE_OPTIONS } from "./AssetEditor.constants";

const AssetEditor = ({
  openAssetLibrary,
  setOpenAssetLibrary,
  // Store
  previewInputProps,
  lastPreviewImages,
  lastPreviewLogos
}) => {
  const assetLibraryPortal = document.getElementById("asset-library-portal");

  const [logoLibrary, setLogoLibrary] = useState(false);
  const [selectedAssetURL, setSelectedAssetURL] = useState("");
  const [selectedLogoAssetURL, setSelectedLogoAssetURL] = useState("");
  const [imageInUse, setImageInUse] = useState("");
  const [logoInUse, setLogoInUse] = useState("");

  const [showMainImage, setShowMainImage] = useState(true);
  const [showLogos, setShowLogos] = useState(true);

  useEffect(() => {
    if (!lastPreviewImages.length) return;

    if (!previewInputProps.backgroundImage) {
      updateInputProps(lastPreviewImages[0]);
    }
  }, [lastPreviewImages]);

  useEffect(() => {
    if (!lastPreviewLogos.length) return;

    if (!previewInputProps.logo) {
      updateLogoInputProps(lastPreviewLogos[0]);
    }
  }, [lastPreviewLogos]);

  useEffect(() => {
    setImageInUse(imageInUse || previewInputProps.backgroundImage);
    setLogoInUse(previewInputProps.logo);
    setSelectedLogoAssetURL(previewInputProps.logo);
  }, [previewInputProps]);

  const changeMultipleInputProps = (keys = [], value) => {
    keys.forEach((key) => {
      ActionsMediaAssetsLab.changePreviewInputProps({ key, value });
      ActionsMediaAssetsLab.changeTemplateInputProps({ key, value });
    });
  };

  const updateSelectedCreative = (creative) => {
    setSelectedAssetURL(creative);
  };

  const updateInputProps = async (asset) => {
    if (isVideo(asset)) {
      const duration = await getVideoDuration(asset);

      ActionsMediaAssetsLab.changePreviewInputProps({ key: "video", value: asset });
      ActionsMediaAssetsLab.changePreviewInputProps({ key: "duration", value: duration });

      ActionsMediaAssetsLab.changeTemplateInputProps({ key: "video", value: asset });
      ActionsMediaAssetsLab.changeTemplateInputProps({ key: "duration", value: duration });
    } else {
      ActionsMediaAssetsLab.changePreviewInputProps({ key: "duration", value: 20 });
      ActionsMediaAssetsLab.changeTemplateInputProps({ key: "duration", value: 20 });

      changeMultipleInputProps(["backgroundImage", "imageUrl"], asset);
    }

    setSelectedAssetURL(asset);
    setImageInUse(asset);
    setOpenAssetLibrary(false);
  };

  const updateLogoInputProps = async (image) => {
    let newLogoSelected = image;

    const { width, height } = await getImageDimensions(image);
    const imageFormat = width > height ? "landscape" : "square";

    if (selectedLogoAssetURL === image) {
      newLogoSelected = "";
    }

    changeMultipleInputProps(["logo", "logoUrl"], newLogoSelected);
    changeMultipleInputProps(["logoFormat"], imageFormat);

    setSelectedLogoAssetURL(newLogoSelected);
    setLogoInUse(newLogoSelected);
    setOpenAssetLibrary(false);
  };

  const closeMediaLibrary = () => {
    setOpenAssetLibrary(false);
    setLogoLibrary(false);
  };

  const saveSelected = async () => {
    const imageAlreadySelected = lastPreviewImages.includes(selectedAssetURL);

    if (imageAlreadySelected) return;

    if (isVideo(selectedAssetURL)) {
      ActionsMediaAssetsLab.changePreviewInputProps({ key: "video", value: selectedAssetURL });
      ActionsMediaAssetsLab.changePreviewInputProps({
        key: "duration",
        value: await getVideoDuration(selectedAssetURL)
      });
    } else {
      ActionsMediaAssetsLab.changePreviewInputProps({
        key: "duration",
        value: 20
      });

      changeMultipleInputProps(["backgroundImage", "imageUrl"], selectedAssetURL);
    }

    ActionsMediaAssetsLab.selectImage(selectedAssetURL);

    setImageInUse(selectedAssetURL);
    setOpenAssetLibrary(false);
  };

  const saveLogoSelected = () => {
    const imageAlreadySelected = lastPreviewLogos.includes(selectedLogoAssetURL);
    if (imageAlreadySelected) return;

    ActionsMediaAssetsLab.selectLogo(selectedLogoAssetURL);
    changeMultipleInputProps(["logo", "logoUrl"], selectedLogoAssetURL);

    setLogoInUse(selectedLogoAssetURL);
    setOpenAssetLibrary(false);
  };

  const updateSelectedLogoCreative = (creative) => {
    setSelectedLogoAssetURL(creative);
  };

  const openLogoMediaLibrary = () => {
    setLogoLibrary(true);
    setOpenAssetLibrary(true);
  };

  const openAssetMediaLibrary = () => {
    setLogoLibrary(false);
    setOpenAssetLibrary(true);
  };

  useEffect(() => {
    ActionsMediaAssetsLab.selectPreviewSelectedAsset(imageInUse);
  }, [imageInUse]);

  const selectedLogoPosition = LOGO_POSITION_OPTIONS.find(
    (option) => option.value === previewInputProps.logoPosition
  );

  const selectedLogoSize = LOGO_SIZE_OPTIONS.find(
    (option) => option.value === previewInputProps.logoSize
  );

  return (
    <>
      <Group direction="column">
        <Collapse
          parent={
            <CollapseHeader
              text="Main image"
              onClick={() => setShowMainImage(!showMainImage)}
              active={showMainImage}
            />
          }
          marginBottom="-10px"
          width="100%"
          show={showMainImage}
        >
          <AssetPreview
            assetList={lastPreviewImages}
            assetInUse={imageInUse}
            emitUpdateInputProps={updateInputProps}
            emitNewAsset={openAssetMediaLibrary}
          />
        </Collapse>

        <Collapse
          parent={
            <CollapseHeader
              text="Logo"
              onClick={() => setShowLogos(!showLogos)}
              active={showLogos}
            />
          }
          marginBottom="-10px"
          width="100%"
          show={showLogos}
        >
          <Group direction="column" gap={"20px"}>
            <AssetPreview
              isLogo
              assetList={lastPreviewLogos}
              assetInUse={logoInUse}
              emitUpdateInputProps={updateLogoInputProps}
              emitNewAsset={openLogoMediaLibrary}
            />

            <Group>
              <SelectInput
                id="logo-position"
                label={"Logo Position"}
                value={selectedLogoPosition}
                type="select"
                options={LOGO_POSITION_OPTIONS}
                onChange={(option) => changeMultipleInputProps(["logoPosition"], option.value)}
                menuPlacement="bottom"
                isSearchable={false}
              />
              <SelectInput
                id="logo-size"
                label={"Logo Size"}
                value={selectedLogoSize}
                type="select"
                options={LOGO_SIZE_OPTIONS}
                onChange={(option) => changeMultipleInputProps(["logoSize"], option.value)}
                menuPlacement="bottom"
                isSearchable={false}
              />
            </Group>
          </Group>
        </Collapse>
      </Group>

      {openAssetLibrary && !logoLibrary && (
        <>
          {createPortal(
            <AssetLibrary
              cancelSelected={closeMediaLibrary}
              saveSelected={saveSelected}
              isMediaLibraryOpen={openAssetLibrary}
              selectedCreative={selectedAssetURL}
              updateSelectedCreative={updateSelectedCreative}
              disableStock={false}
              allowVideos={true}
              allowImages={true}
              canBuyStockPicture={false}
              canBeEmpty={false}
              isLogo={false}
            />,
            assetLibraryPortal
          )}
        </>
      )}

      {openAssetLibrary && logoLibrary && (
        <>
          {createPortal(
            <AssetLibrary
              cancelSelected={closeMediaLibrary}
              saveSelected={saveLogoSelected}
              isMediaLibraryOpen={openAssetLibrary}
              selectedCreative={selectedLogoAssetURL}
              updateSelectedCreative={updateSelectedLogoCreative}
              disableStock={false}
              allowVideos={true}
              allowImages={true}
              canBuyStockPicture={false}
              canBeEmpty={false}
              isLogo={true}
            />,
            assetLibraryPortal
          )}
        </>
      )}
    </>
  );
};

export default withRefluxStore(
  AssetEditor,
  [MediaAssetsLabStore],
  ["previewInputProps", "lastPreviewImages", "lastPreviewLogos"]
);
